import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { waLink } from "@/lib/scroll";

const REGIONES = [
  {
    id: "norte",
    name: "Norte Integrado",
    page: "/zonas/montero-mineros",
    towns: [
      ["Montero", -17.339, -63.251],
      ["Mineros", -17.12, -63.23],
      ["Warnes", -17.51, -63.16],
      ["Saavedra", -17.23, -63.19],
      ["Puerto Fernández Alonso", -17.6, -63.35],
      ["Portachuelo", -17.36, -63.39],
      ["Santa Rosa del Sara", -17.107, -63.599],
      ["Buena Vista", -17.458, -63.659],
      ["San Carlos", -17.405, -63.734],
      ["Yapacaní", -17.4, -63.833],
      ["San Juan de Yapacaní", -17.217, -63.84],
    ],
    cultivos: ["Caña", "Soya", "Maíz", "Girasol"],
    producto: "Madurante de caña, fungicidas para roya e insecticidas de choque.",
    ahorro: [
      "Caña alta sin que entre equipo terrestre",
      "Registro de vuelo por lote",
    ],
  },
  {
    id: "nordeste",
    name: "Nordeste",
    page: "/zonas/san-julian",
    towns: [
      ["San Julián", -16.917, -62.617],
      ["San Ramón", -16.633, -62.517],
      ["El Puente", -17.317, -62.45],
      ["Okinawa", -17.23, -62.89],
    ],
    cultivos: ["Soya", "Sorgo", "Trigo"],
    producto: "Fungicidas para roya e insecticidas; campaña fuerte jun–ago.",
    ahorro: [
      "Cero pisoteo de surcos",
      "Aplicación en la ventana justa",
      "Cotización por hectárea en el día",
    ],
  },
  {
    id: "este4",
    name: "Este — Cuatro Cañadas",
    page: "/zonas/cuatro-canadas",
    towns: [
      ["Cuatro Cañadas", -17.217, -62.35],
      ["La Bélgica", -17.283, -62.417],
      ["Los Troncos", -17.117, -62.25],
    ],
    cultivos: ["Soya", "Maíz"],
    producto: "Fungicida para roya asiática e insecticida para orugas y cogollero.",
    ahorro: [
      "Sin pérdida por rodado (2–5 % del lote)",
      "Aplicación en la ventana justa de la plaga",
      "Llegada coordinada en 48 horas",
    ],
  },
  {
    id: "pailon",
    name: "Pailón",
    page: "/zonas/pailon",
    towns: [
      ["Pailón", -17.65, -62.75],
      ["Pailón Sur", -17.72, -62.77],
      ["Cotoca", -17.75, -62.717],
      ["Puerto Pailas", -17.63, -62.55],
    ],
    cultivos: ["Caña de azúcar", "Soya", "Pasturas"],
    producto: "Madurantes y herbicidas en caña; fertilización foliar en pastura.",
    ahorro: [
      "Sin daño de rodado en caña en pie",
      "Dosis pareja por hectárea",
      "Ficha técnica firmada al cierre",
    ],
  },
  {
    id: "transcom",
    name: "Este — Transcom",
    page: null,
    towns: [
      ["Cañada Larga", -17.62, -62.85],
      ["Tres Cruces", -17.83, -62.88],
      ["Pozo del Tigre", -17.78, -62.53],
      ["Tunas", -17.6, -62.62],
      ["El Tinto", -18.02, -63.25],
    ],
    cultivos: ["Soya", "Maíz", "Pasturas"],
    producto: "Fungicidas e insecticidas de campaña; fertilización foliar.",
    ahorro: [
      "Entrás al lote aunque esté barroso",
      "Cotización por hectárea el mismo día",
    ],
  },
  {
    id: "scz",
    name: "SCZ y alrededores",
    page: "/zonas/santa-cruz",
    towns: [
      ["Santa Cruz de la Sierra", -17.783, -63.182],
      ["La Guardia", -17.883, -63.317],
      ["El Torno", -17.983, -63.383],
      ["Porongo", -17.85, -63.31],
    ],
    cultivos: ["Hortícolas", "Pasturas", "Cítricos"],
    producto: "Foliares y fungicidas en hortícolas; fertilización de pastura.",
    ahorro: [
      "Aplicación precisa en lotes chicos cerca de la ciudad",
      "Cero pisoteo en quintas de producción",
      "Respuesta en el día por cercanía a la base",
    ],
  },
  {
    id: "chiquitania",
    name: "Chiquitania",
    page: "/zonas/chiquitania",
    towns: [
      ["San José de Chiquitos", -17.85, -60.75],
      ["Roboré", -18.33, -59.76],
      ["Santiago de Chiquitos", -18.33, -59.6],
      ["San Ignacio de Velasco", -16.367, -60.967],
      ["San Rafael de Velasco", -16.783, -60.683],
      ["San Miguel de Velasco", -16.7, -60.964],
      ["Concepción", -16.133, -62.017],
      ["San Javier", -16.275, -62.471],
      ["San Antonio de Lomerío", -16.783, -61.867],
      ["Ascensión de Guarayos", -15.717, -63.083],
      ["Urubichá", -15.65, -62.95],
      ["El Puente de Guarayos", -16.283, -62.883],
      ["San Matías", -16.367, -58.4],
      ["Puerto Suárez", -18.967, -57.8],
      ["Puerto Quijarro", -17.783, -57.767],
      ["El Carmen Rivero Tórrez", -17.85, -58.167],
    ],
    cultivos: ["Girasol", "Sorgo", "Soya de verano"],
    producto: "Insecticidas y fungicidas foliares de campaña.",
    ahorro: [
      "Aplicación nocturna con menos deriva",
      "Traslado coordinado por volumen",
      "Precio por hectárea el mismo día",
    ],
  },
  {
    id: "sur",
    name: "Sur",
    page: null,
    towns: [
      ["Samaipata", -18.18, -63.87],
      ["Mairana", -18.117, -63.967],
      ["Quirusillas", -18.333, -63.933],
      ["Pampa Grande", -18.083, -64.1],
      ["Comarapa", -17.917, -64.517],
      ["Saipina", -18.1, -64.583],
      ["Vallegrande", -18.483, -64.1],
      ["Pucará", -18.367, -64.117],
      ["Postrervalle", -18.183, -64.133],
      ["Trigal", -18.383, -64.217],
      ["Moro Moro", -18.533, -64.083],
      ["Cabezas", -18.783, -63.317],
      ["Gutiérrez", -18.217, -63.183],
      ["Camiri", -20.033, -63.517],
      ["Charagua", -19.833, -63.2],
      ["Boyuibe", -20.417, -63.283],
      ["Cuevo", -20.467, -63.517],
      ["Lagunillas", -19.65, -63.683],
    ],
    cultivos: ["Zona en evaluación"],
    producto: null,
    ahorro: [
      "Se coordina por volumen de hectáreas",
      "Juntamos varios lotes y armamos la fecha",
    ],
    note: "Todavía no operamos fijo en el sur: escribinos y coordinamos la logística.",
  },
];

const makeIcon = (active) =>
  L.divIcon({
    className: "",
    html: `<div ${active ? 'class="sonar-dot"' : ""} style="width:14px;height:14px;border-radius:9999px;background:${active ? "#00A651" : "#151A1F"};border:2px solid #F6F7F5;box-shadow:0 2px 8px rgba(0,0,0,0.35);"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });

const FlyTo = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target.coords, target.zoom, { duration: 1.4 });
  }, [target, map]);
  return null;
};

let mapaIntroFired = false;

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const MapaZonas = () => {
  const [active, setActive] = useState("norte");
  const [town, setTown] = useState("Montero");
  const [target, setTarget] = useState(null);
  const region = REGIONES.find((r) => r.id === active);

  const select = (regionId, townName) => {
    setActive(regionId);
    if (townName) {
      setTown(townName);
      const r = REGIONES.find((x) => x.id === regionId);
      const t = r.towns.find(([name]) => name === townName);
      if (t) setTarget({ coords: [t[1], t[2]], zoom: 10 });
    }
  };

  const pickTown = (t) => {
    setTown(t[0]);
    setTarget({ coords: [t[1], t[2]], zoom: 11 });
  };

  return (
    <section id="zonas" data-testid="zonas-section" className="bg-[#F6F7F5] text-[#151A1F] py-24 sm:py-32 border-t border-[#151A1F]/10">
      <div className="mx-auto max-w-7xl px-6 sm:px-10">
        <motion.p {...reveal} className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#046A38]">
          Mapa de cobertura · Santa Cruz
        </motion.p>
        <motion.h2
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.1 }}
          className="font-display font-bold tracking-tight leading-[1.08] text-2xl sm:text-4xl lg:text-5xl mt-6 max-w-3xl"
        >
          Apretá tu zona. Mirá qué se aplica y qué ahorrás.
        </motion.h2>

        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.12 }}
          onViewportEnter={() => {
            if (!mapaIntroFired) {
              mapaIntroFired = true;
              setTimeout(() => setTarget({ coords: [-17.36, -63.28], zoom: 9 }), 600);
            }
          }}
          className="mt-10 flex flex-wrap gap-2"
        >
          {REGIONES.map((r) => (
            <button
              key={r.id}
              data-testid={`mapa-region-${r.id}`}
              onClick={() => select(r.id, r.towns[0][0])}
              className={`font-mono-tech text-[10px] uppercase tracking-[0.2em] rounded-full px-4 py-2 border transition-all duration-300 ${
                active === r.id
                  ? "bg-[#151A1F] text-[#F6F7F5] border-[#151A1F]"
                  : "border-[#151A1F]/15 text-[#151A1F]/60 hover:border-[#046A38]/50"
              }`}
            >
              {r.name}
            </button>
          ))}
        </motion.div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.div {...reveal} transition={{ ...reveal.transition, delay: 0.15 }} className="lg:col-span-7">
            <div className="mapa-fumiga rounded-sm overflow-hidden border border-[#151A1F]/10">
              <MapContainer
                center={[-17.9, -61.6]}
                zoom={7}
                scrollWheelZoom={false}
                className="h-[420px] sm:h-[560px] w-full z-0"
                data-testid="mapa-real"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>'
                  url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FlyTo target={target} />
                {REGIONES.flatMap((r) =>
                  r.towns.map(([t, lat, lng]) => (
                    <Marker
                      key={t}
                      position={[lat, lng]}
                      icon={makeIcon(active === r.id)}
                      eventHandlers={{ click: () => select(r.id, t) }}
                    />
                  ))
                )}
              </MapContainer>
            </div>
            <p className="mt-4 font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#6B7280] inline-flex items-center gap-2">
              <MapPin size={12} /> {REGIONES.reduce((n, r) => n + r.towns.length, 0)} localidades con cobertura · Apretá un punto del mapa
            </p>
          </motion.div>

          <div className="lg:col-span-5 lg:sticky lg:top-24" style={{ perspective: 1200 }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={active + town}
                data-testid="mapa-panel"
                initial={{ opacity: 0, rotateY: 16, scale: 0.96 }}
                animate={{ opacity: 1, rotateY: 0, scale: 1 }}
                exit={{ opacity: 0, rotateY: -10, scale: 0.98 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                style={{ transformPerspective: 1200 }}
                className="border border-[#151A1F]/10 bg-white p-8 sm:p-10"
              >
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#046A38]">
                  Punto seleccionado · {town}
                </p>
                <h3 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-3">{region.name}</h3>

                <div className="mt-6">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Localidades</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {region.towns.map(([t]) => (
                      <button
                        key={t}
                        data-testid={`mapa-town-${t.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        onClick={() => pickTown(t)}
                        className={`font-mono-tech text-[10px] uppercase tracking-[0.15em] rounded-full px-3 py-1 border transition-colors duration-300 ${
                          town === t ? "border-[#046A38] text-[#046A38]" : "border-[#151A1F]/15 text-[#151A1F]/60 hover:border-[#046A38]/40"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#046A38]">Cultivos fuertes</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {region.cultivos.map((c) => (
                      <span key={c} className="font-mono-tech text-[10px] uppercase tracking-[0.15em] border border-[#151A1F]/15 rounded-full px-3 py-1 text-[#151A1F]/60">
                        {c}
                      </span>
                    ))}
                  </div>
                </div>

                {region.producto && (
                  <div className="mt-6">
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#046A38]">Producto típico de la zona</p>
                    <p className="mt-3 text-sm text-[#151A1F]/75 leading-relaxed">{region.producto}</p>
                  </div>
                )}

                <div className="mt-6">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#046A38]">Lo que ahorrás con el dron</p>
                  <ul className="mt-3 space-y-2.5">
                    {region.ahorro.map((a) => (
                      <li key={a} className="flex items-start gap-3 text-sm text-[#151A1F]/80">
                        <span className="mt-1.5 w-2 h-2 shrink-0 bg-[#00A651]" aria-hidden="true" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>

                {region.note && <p className="mt-6 text-sm text-[#151A1F]/60 leading-relaxed">{region.note}</p>}

                <div className="mt-8 flex flex-wrap gap-3">
                  <a
                    data-testid="mapa-whatsapp-cta"
                    href={waLink(`Hola Fumiga, quiero cotizar aplicación con dron en ${town}.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#151A1F] text-[#F6F7F5] font-display font-semibold text-sm px-6 py-3 rounded-full hover:bg-[#046A38] transition-colors duration-300"
                  >
                    Cotizar en {town} <ArrowUpRight size={16} />
                  </a>
                  {region.page && (
                    <Link
                      data-testid="mapa-zona-page-link"
                      to={region.page}
                      className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#046A38] px-2 py-3 hover:gap-3 transition-all duration-300"
                    >
                      Ver página de la zona →
                    </Link>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <motion.p {...reveal} transition={{ ...reveal.transition, delay: 0.2 }} className="mt-10 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#6B7280] leading-relaxed">
          Cifras de referencia de la categoría · El ahorro exacto se calcula en tu lote
        </motion.p>
      </div>
    </section>
  );
};

export default MapaZonas;
